import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
  TimelineSeparator,
} from '@mui/lab';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas, far } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useState } from 'react';

export const ApprRouteProgressDialog = ({ data, closeDialog }) => {
  const [stepSpecs, setStepSpecs] = useState();
  const theme = useTheme();
  const rsrc = useRsrc();

  useEffect(() => {
    const reqParam = { apprJobId: data.apprJobId };
    rsrc.data('admin', 'APPR_ROUTE_PROGRESS_DATA_PLUGIN', reqParam).then((stepSpecs) => {
      setStepSpecs(stepSpecs);
    });
  }, [data.apprJobId, rsrc]);

  // Solid colors for different progress statuses
  const getStepColor = (progress) => {
    switch (progress) {
      case 'APPROVED':
        return '#4caf50'; // green
      case 'APPROVAL_PENDING':
        return '#ffeb3b'; // yellow
      default:
        return '#9e9e9e'; // grey for NOT_STARTED
    }
  };

  // Icons for the timeline steps
  const getStepIcon = (progress) => {
    switch (progress) {
      case 'APPROVED':
        return fas.faCheck;
      case 'APPROVAL_PENDING':
        return fas.faStamp; // using stamp for pending
      default:
        return far.faCircle;
    }
  };

  // For the user boxes: solid border and icon colors
  const getUserColor = (progress) => {
    switch (progress) {
      case 'APPROVED':
        return '#4caf50';
      case 'APPROVAL_PENDING':
        return '#ffeb3b';
      default:
        return '#9e9e9e';
    }
  };

  // Icons for user boxes
  const getUserIcon = (progress) => {
    switch (progress) {
      case 'APPROVED':
        return fas.faCheck;
      case 'APPROVAL_PENDING':
        return fas.faStamp;
      default:
        return far.faCircle;
    }
  };

  // Pale background color for user boxes (for light mode)
  const getPaleColor = (progress) => {
    switch (progress) {
      case 'APPROVED':
        return '#e8f5e9'; // pale green
      case 'APPROVAL_PENDING':
        return '#fffde7'; // pale yellow
      default:
        return '#f5f5f5'; // pale grey
    }
  };

  return (
    stepSpecs && (
      <Dialog onClose={closeDialog} open={true} fullWidth maxWidth="md">
        <DialogTitle>Approval Route Progress</DialogTitle>
        <DialogContent>
          <Timeline
            sx={{
              [`& .${timelineOppositeContentClasses.root}`]: {
                flex: 0.2,
              },
            }}
          >
            {stepSpecs.map((step, index) => (
              <TimelineItem key={index}>
                <TimelineOppositeContent color="textSecondary">
                  Step {index + 1}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot
                    variant="filled"
                    sx={{
                      backgroundColor: getStepColor(step.progress),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon icon={getStepIcon(step.progress)} />
                  </TimelineDot>
                  {index < stepSpecs.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: theme.spacing(1),
                    }}
                  >
                    {step.userApprSpecs.map((user, uIndex) => (
                      <div
                        key={uIndex}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? alpha(getUserColor(user.progress), 0.1)
                              : getPaleColor(user.progress),
                          border: '1px solid',
                          borderColor: getUserColor(user.progress),
                          borderRadius: theme.spacing(3),
                          padding: '8px',
                          minWidth: '200px',
                        }}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: getUserColor(user.progress),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '8px',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={getUserIcon(user.progress)}
                            style={{ color: '#fff' }}
                          />
                        </div>
                        <div>
                          <div>
                            <strong>
                              {user.firstname} {user.lastname}
                            </strong>
                          </div>
                          <div>
                            {user.username}, {user.apprGroup}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  );
};